/**
 * /assistants/:agentId/edit — the assistant builder (hidden: AgentStudioRoute).
 *
 * Tabs: Instructions (editor + read-only guardrails note) · Model (provider /
 * model / temperature from the server catalog) · Context (file uploads with
 * status chips) · Skills (markdown playbook CRUD) · Connectors (toggle
 * approved connectors, request custom MCP ones) · Danger zone.
 *
 * Templates are read-only for non-SuperAdmins — the UI pushes users to clone.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box, Heading, Text, HStack, VStack, Button, IconButton, Badge, Spinner, useToast,
  useColorModeValue, Tabs, TabList, TabPanels, Tab, TabPanel, FormControl, FormLabel,
  Input, Textarea, Select, Slider, SliderTrack, SliderFilledTrack, SliderThumb,
  Switch, Table, Thead, Tbody, Tr, Th, Td, Tag, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, ModalCloseButton, useDisclosure, Alert, AlertIcon, Code,
} from '@chakra-ui/react';
import { FiArrowLeft, FiMessageSquare, FiSave, FiUpload, FiTrash2, FiPlus, FiEdit2 } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { callAgentStudio, uploadContextFile } from '../../hooks/useAssistants';

const statusColor = { processing: 'yellow', ready: 'green', error: 'red' };

export default function AssistantBuilderPage() {
  const { agentId } = useParams();
  const history = useHistory();
  const toast = useToast();
  const { currentUser } = useAuth();
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const subtle = useColorModeValue('gray.600', 'gray.400');
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);
  const [skills, setSkills] = useState([]);
  const [files, setFiles] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [connectors, setConnectors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // editable state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [config, setConfig] = useState({});
  const [connectorKeys, setConnectorKeys] = useState([]);

  const skillModal = useDisclosure();
  const connectorModal = useDisclosure();
  const [skillForm, setSkillForm] = useState({ id: null, name: '', description: '', body: '' });
  const [connectorForm, setConnectorForm] = useState({ key: '', label: '', description: '', url: '' });

  const readOnly = agent?.is_template && !currentUser?.SuperAdmin;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [detail, cat, conns] = await Promise.all([
        callAgentStudio('getAssistant', { id: agentId }),
        callAgentStudio('listCatalog'),
        callAgentStudio('listConnectors'),
      ]);
      setAgent(detail.agent);
      setSkills(detail.skills || []);
      setFiles(detail.contextFiles || []);
      setCatalog(cat);
      setConnectors(conns.connectors || []);
      setName(detail.agent.name || '');
      setDescription(detail.agent.description || '');
      setInstructions(detail.agent.instructions || '');
      setConfig(detail.agent.config || {});
      setConnectorKeys(detail.agent.connector_keys || []);
    } catch (err) {
      toast({ title: 'Could not load assistant', description: err.message, status: 'error', duration: 6000 });
      history.push('/assistants');
    } finally {
      setLoading(false);
    }
  }, [agentId, history, toast]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { agent: updated } = await callAgentStudio('updateAssistant', { id: agentId, patch });
      setAgent(updated);
      toast({ title: 'Saved', status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = () => save({ name, description, instructions, config, connectorKeys });

  const handleUpload = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const row = await uploadContextFile(currentUser.uid, agentId, file);
      setFiles((prev) => [...prev, row]);
      toast({
        title: row.status === 'ready' ? 'File ready' : `File ${row.status}`,
        description: row.status === 'error' ? row.extract_error : `${row.name} — ${row.extractedChars || 0} characters extracted`,
        status: row.status === 'ready' ? 'success' : 'warning',
        duration: 5000,
      });
    } catch (err) {
      toast({ title: 'Upload failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteFile = async (file) => {
    try {
      await callAgentStudio('deleteContextFile', { fileId: file.id });
      setFiles((prev) => prev.filter((f) => f.id !== file.id));
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  const openSkillModal = (skill) => {
    setSkillForm(skill
      ? { id: skill.id, name: skill.name, description: skill.description || '', body: skill.body || '' }
      : { id: null, name: '', description: '', body: '' });
    skillModal.onOpen();
  };

  const saveSkill = async () => {
    if (!skillForm.name.trim() || !skillForm.body.trim()) {
      toast({ title: 'Name and playbook are required', status: 'warning', duration: 3000 });
      return;
    }
    try {
      if (skillForm.id) {
        const { skill } = await callAgentStudio('updateSkill', {
          skillId: skillForm.id,
          patch: { name: skillForm.name, description: skillForm.description, body: skillForm.body },
        });
        setSkills((prev) => prev.map((s) => (s.id === skill.id ? skill : s)));
      } else {
        const { skill } = await callAgentStudio('createSkill', {
          agentId,
          skill: { name: skillForm.name, description: skillForm.description, body: skillForm.body, position: skills.length },
        });
        setSkills((prev) => [...prev, skill]);
      }
      skillModal.onClose();
    } catch (err) {
      toast({ title: 'Skill save failed', description: err.message, status: 'error', duration: 6000 });
    }
  };

  const toggleSkill = async (skill) => {
    try {
      const { skill: updated } = await callAgentStudio('updateSkill', { skillId: skill.id, patch: { enabled: !skill.enabled } });
      setSkills((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    } catch (err) {
      toast({ title: 'Toggle failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  const deleteSkill = async (skill) => {
    try {
      await callAgentStudio('deleteSkill', { skillId: skill.id });
      setSkills((prev) => prev.filter((s) => s.id !== skill.id));
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  const requestConnector = async () => {
    if (!connectorForm.label.trim() || !connectorForm.url.trim()) {
      toast({ title: 'Label and MCP URL are required', status: 'warning', duration: 3000 });
      return;
    }
    try {
      await callAgentStudio('requestConnector', {
        connector: {
          key: connectorForm.key || connectorForm.label,
          label: connectorForm.label,
          description: connectorForm.description,
          manifest: { url: connectorForm.url },
        },
      });
      connectorModal.onClose();
      setConnectorForm({ key: '', label: '', description: '', url: '' });
      toast({ title: 'Connector requested', description: 'Mitchell must approve it before assistants can use it.', status: 'info', duration: 6000 });
      const conns = await callAgentStudio('listConnectors');
      setConnectors(conns.connectors || []);
    } catch (err) {
      toast({ title: 'Request failed', description: err.message, status: 'error', duration: 6000 });
    }
  };

  const handleDelete = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${agent.name}" and all its chats, skills, and files? This cannot be undone.`)) return;
    try {
      await callAgentStudio('deleteAssistant', { id: agentId });
      toast({ title: 'Deleted', status: 'info', duration: 3000 });
      history.push('/assistants');
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 6000 });
    }
  };

  if (loading) {
    return <Box minH="100vh" bg={pageBg} display="flex" alignItems="center" justifyContent="center"><Spinner size="xl" /></Box>;
  }
  if (!agent) return null;

  const providerCatalog = catalog?.models?.[config.provider || 'gemini'];
  const approvedConnectors = connectors.filter((c) => c.status === 'approved');

  return (
    <Box minH="100vh" bg={pageBg} p={6}>
      <Box maxW="900px" mx="auto">
        <HStack mb={4} justify="space-between">
          <HStack>
            <IconButton icon={<FiArrowLeft />} aria-label="Back" variant="ghost" onClick={() => history.push('/assistants')} />
            <Heading size="md">{agent.name}</Heading>
            {agent.is_template && <Badge colorScheme="blue">Template</Badge>}
          </HStack>
          <HStack>
            <Button size="sm" leftIcon={<FiMessageSquare />} onClick={() => history.push(`/assistants/${agentId}/chat`)}>Chat</Button>
            {!readOnly && (
              <Button size="sm" colorScheme="blue" leftIcon={<FiSave />} onClick={saveProfile} isLoading={saving}>Save changes</Button>
            )}
          </HStack>
        </HStack>

        {readOnly && (
          <Alert status="info" borderRadius="md" mb={4} fontSize="sm">
            <AlertIcon />
            This is a company template — clone it from the Assistants page to customize your own copy.
          </Alert>
        )}

        <Tabs colorScheme="blue" variant="enclosed">
          <TabList>
            <Tab>Instructions</Tab>
            <Tab>Model</Tab>
            <Tab>Context ({files.length})</Tab>
            <Tab>Skills ({skills.length})</Tab>
            <Tab>Connectors</Tab>
            {!readOnly && <Tab color="red.400">Danger</Tab>}
          </TabList>

          <TabPanels bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderTop="none" borderRadius="0 0 8px 8px">
            {/* ------------------------------------------------- Instructions */}
            <TabPanel>
              <VStack align="stretch" spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">Name</FormLabel>
                  <Input value={name} onChange={(e) => setName(e.target.value)} isDisabled={readOnly} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Description</FormLabel>
                  <Input value={description} onChange={(e) => setDescription(e.target.value)} isDisabled={readOnly} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Instructions</FormLabel>
                  <Textarea
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    rows={14}
                    fontFamily="mono"
                    fontSize="sm"
                    isDisabled={readOnly}
                    placeholder="How should this assistant behave? Role, tone, priorities, workflows…"
                  />
                </FormControl>
                <Alert status="info" borderRadius="md" fontSize="xs">
                  <AlertIcon />
                  Non-editable platform guardrails are always layered on top: mailbox binding to the signed-in user, approval cards for
                  sends/deletes/filters, prompt-injection defenses, and automatic signatures. Your instructions shape everything else.
                </Alert>
              </VStack>
            </TabPanel>

            {/* -------------------------------------------------------- Model */}
            <TabPanel>
              <VStack align="stretch" spacing={5} maxW="520px">
                <FormControl>
                  <FormLabel fontSize="sm">Provider</FormLabel>
                  <Select
                    value={config.provider || 'gemini'}
                    onChange={(e) => setConfig({ ...config, provider: e.target.value, model: catalog?.models?.[e.target.value]?.defaultModel || null })}
                    isDisabled={readOnly}
                  >
                    {Object.entries(catalog?.models || {}).map(([key, p]) => (
                      <option key={key} value={key}>{p.label}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Model</FormLabel>
                  <Select
                    value={config.model || providerCatalog?.defaultModel || ''}
                    onChange={(e) => setConfig({ ...config, model: e.target.value })}
                    isDisabled={readOnly}
                  >
                    {(providerCatalog?.models || []).map((m) => (
                      <option key={m.id} value={m.id}>{m.label}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Creativity (temperature): {(config.temperature ?? 0.3).toFixed(2)}</FormLabel>
                  <Slider
                    value={config.temperature ?? 0.3}
                    min={0} max={1} step={0.05}
                    onChange={(v) => setConfig({ ...config, temperature: v })}
                    isDisabled={readOnly}
                  >
                    <SliderTrack><SliderFilledTrack /></SliderTrack>
                    <SliderThumb />
                  </Slider>
                  <HStack justify="space-between"><Text fontSize="xs" color={subtle}>Precise</Text><Text fontSize="xs" color={subtle}>Creative</Text></HStack>
                </FormControl>
                <Text fontSize="xs" color={subtle}>
                  Daily budget: ${Number(config.daily_cost_cap_usd ?? 5).toFixed(2)} of estimated model spend per user per 24h (admin-set).
                </Text>
              </VStack>
            </TabPanel>

            {/* ------------------------------------------------------ Context */}
            <TabPanel>
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between">
                  <Text fontSize="sm" color={subtle}>
                    Knowledge files the assistant can read on demand (txt, md, csv, json, pdf, images — 20MB max).
                  </Text>
                  {!readOnly && (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        accept=".txt,.md,.csv,.json,.pdf,image/*,text/*"
                        onChange={(e) => handleUpload(e.target.files)}
                      />
                      <Button size="sm" leftIcon={<FiUpload />} onClick={() => fileInputRef.current?.click()} isLoading={uploading}>
                        Upload file
                      </Button>
                    </>
                  )}
                </HStack>
                {files.length === 0 ? (
                  <Text fontSize="sm" color={subtle}>No context files yet.</Text>
                ) : (
                  <Table size="sm">
                    <Thead><Tr><Th>Name</Th><Th>Status</Th><Th>Extracted</Th><Th /></Tr></Thead>
                    <Tbody>
                      {files.map((f) => (
                        <Tr key={f.id}>
                          <Td fontSize="sm">{f.name}</Td>
                          <Td><Tag size="sm" colorScheme={statusColor[f.status] || 'gray'}>{f.status}</Tag></Td>
                          <Td fontSize="xs" color={subtle}>{f.status === 'error' ? (f.extract_error || 'failed') : `${(f.extractedChars || 0).toLocaleString()} chars`}</Td>
                          <Td textAlign="right">
                            {!readOnly && (
                              <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red" aria-label="Delete file" onClick={() => deleteFile(f)} />
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </VStack>
            </TabPanel>

            {/* ------------------------------------------------------- Skills */}
            <TabPanel>
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between">
                  <Text fontSize="sm" color={subtle}>
                    Reusable playbooks. The assistant sees each skill&apos;s name + description and loads the full playbook when relevant.
                  </Text>
                  {!readOnly && (
                    <Button size="sm" leftIcon={<FiPlus />} onClick={() => openSkillModal(null)}>New skill</Button>
                  )}
                </HStack>
                {skills.length === 0 ? (
                  <Text fontSize="sm" color={subtle}>No skills yet.</Text>
                ) : (
                  <Table size="sm">
                    <Thead><Tr><Th>Skill</Th><Th>Description</Th><Th>On</Th><Th /></Tr></Thead>
                    <Tbody>
                      {skills.map((s) => (
                        <Tr key={s.id}>
                          <Td fontSize="sm" fontWeight="600">{s.name}</Td>
                          <Td fontSize="xs" color={subtle} maxW="300px"><Text noOfLines={2}>{s.description}</Text></Td>
                          <Td><Switch size="sm" isChecked={s.enabled} onChange={() => toggleSkill(s)} isDisabled={readOnly} /></Td>
                          <Td textAlign="right">
                            {!readOnly && (
                              <HStack justify="flex-end" spacing={1}>
                                <IconButton icon={<FiEdit2 />} size="xs" variant="ghost" aria-label="Edit skill" onClick={() => openSkillModal(s)} />
                                <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red" aria-label="Delete skill" onClick={() => deleteSkill(s)} />
                              </HStack>
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </VStack>
            </TabPanel>

            {/* --------------------------------------------------- Connectors */}
            <TabPanel>
              <VStack align="stretch" spacing={4}>
                <Text fontSize="sm" color={subtle}>
                  Integrations this assistant may use. Gmail always operates on YOUR mailbox only. Custom connectors must be approved by Mitchell before they appear here.
                </Text>
                {approvedConnectors.map((c) => (
                  <HStack key={c.key} justify="space-between" borderWidth="1px" borderColor={cardBorder} borderRadius="md" p={3}>
                    <Box>
                      <HStack><Text fontSize="sm" fontWeight="600">{c.label}</Text><Badge fontSize="2xs">{c.kind}</Badge></HStack>
                      <Text fontSize="xs" color={subtle}>{c.description}</Text>
                    </Box>
                    <Switch
                      isChecked={connectorKeys.includes(c.key)}
                      isDisabled={readOnly}
                      onChange={(e) => setConnectorKeys(e.target.checked
                        ? [...connectorKeys, c.key]
                        : connectorKeys.filter((k) => k !== c.key))}
                    />
                  </HStack>
                ))}
                {connectors.filter((c) => c.status === 'pending').length > 0 && (
                  <Box>
                    <Text fontSize="xs" color={subtle} mb={1}>Awaiting approval:</Text>
                    {connectors.filter((c) => c.status === 'pending').map((c) => (
                      <Tag key={c.key} size="sm" colorScheme="yellow" mr={2}>{c.label}</Tag>
                    ))}
                  </Box>
                )}
                {!readOnly && (
                  <Button size="sm" variant="outline" leftIcon={<FiPlus />} onClick={connectorModal.onOpen} alignSelf="start">
                    Request custom connector (MCP)
                  </Button>
                )}
                <Text fontSize="xs" color={subtle}>
                  Remember to hit <b>Save changes</b> after toggling connectors.
                </Text>
              </VStack>
            </TabPanel>

            {/* ------------------------------------------------------- Danger */}
            {!readOnly && (
              <TabPanel>
                <VStack align="stretch" spacing={3} maxW="520px">
                  <Text fontSize="sm" color={subtle}>Deleting an assistant removes all of its chats, skills, and context files permanently.</Text>
                  <Button colorScheme="red" variant="outline" leftIcon={<FiTrash2 />} onClick={handleDelete} alignSelf="start">
                    Delete this assistant
                  </Button>
                </VStack>
              </TabPanel>
            )}
          </TabPanels>
        </Tabs>
      </Box>

      {/* Skill editor modal */}
      <Modal isOpen={skillModal.isOpen} onClose={skillModal.onClose} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{skillForm.id ? 'Edit skill' : 'New skill'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">Name</FormLabel>
                <Input value={skillForm.name} onChange={(e) => setSkillForm({ ...skillForm, name: e.target.value })} placeholder="e.g. Weekly Pipeline Recap" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Description (the assistant uses this to decide when to load the skill)</FormLabel>
                <Input value={skillForm.description} onChange={(e) => setSkillForm({ ...skillForm, description: e.target.value })} placeholder="When the user asks for…" />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Playbook (markdown)</FormLabel>
                <Textarea
                  value={skillForm.body}
                  onChange={(e) => setSkillForm({ ...skillForm, body: e.target.value })}
                  rows={12}
                  fontFamily="mono"
                  fontSize="sm"
                  placeholder={'## Goal\n…\n\n## Steps\n1. …'}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={skillModal.onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={saveSkill}>Save skill</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Connector request modal */}
      <Modal isOpen={connectorModal.isOpen} onClose={connectorModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Request a custom connector</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Alert status="warning" borderRadius="md" fontSize="xs">
                <AlertIcon />
                Connectors are MCP servers. Every request is reviewed and approved by Mitchell before any assistant can use it; API tokens are stored in server config, never in the request.
              </Alert>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Label</FormLabel>
                <Input value={connectorForm.label} onChange={(e) => setConnectorForm({ ...connectorForm, label: e.target.value })} placeholder="e.g. Jira" />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">MCP endpoint URL</FormLabel>
                <Input value={connectorForm.url} onChange={(e) => setConnectorForm({ ...connectorForm, url: e.target.value })} placeholder="https://…/mcp" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">What is it for?</FormLabel>
                <Textarea value={connectorForm.description} onChange={(e) => setConnectorForm({ ...connectorForm, description: e.target.value })} rows={3} />
              </FormControl>
              <Text fontSize="xs" color={subtle}>Key (optional): <Code fontSize="xs">{connectorForm.key || '(derived from label)'}</Code></Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={connectorModal.onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={requestConnector}>Submit request</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
